import { environment } from '../../../environments/environment';
import { Solution, SolutionOption, WorkspaceSolution } from '../models/solution.model';

/**
 * Catálogo local de soluciones, equivalente al `modules` de la navegación de
 * myservy-dashboard.
 *
 * El backend devuelve qué soluciones tiene habilitadas el workspace, pero todavía no su
 * icono ni su url: eso sale de aquí.
 *
 * Solo se declara destino para las soluciones que tienen un front que sabe recibir la
 * sesión por enlace. Las demás se listan como "No disponible": la consola no publica una
 * ruta por solución —sus rutas son `/dashboard`, `/users`, `/profile`, `/workspace` y
 * `/notification`—, así que enlazar a `/<solución>` dejaba al usuario en un SPA sin ruta
 * que resolver.
 */
interface CatalogEntry {
  /** Clave del catálogo del dashboard; también sirve para casar por `solutionCode`. */
  key: string;
  name: string;
  iconName: string;
  /** Front propio de la solución. */
  externalUrl?: string;
  /** Esta misma app: se lista, pero no se navega a ella. */
  isCurrent?: boolean;
}

const SOLUTION_CATALOG: CatalogEntry[] = [
  { key: 'knowledgeEngine', name: 'Knowledge Engine', iconName: 'psychology' },
  { key: 'serviceEcommerce', name: 'Service eCommerce', iconName: 'storefront' },
  { key: 'hksdv', name: 'HKSDV', iconName: 'verified_user' },
  { key: 'knowledgeManager', name: 'Knowledge Manager', iconName: 'menu_book' },
  {
    key: 'maintenanceOperations',
    name: 'Maintenance Operations',
    iconName: 'build',
    // Tiene front propio: mandarla a la consola dejaba al usuario en el dashboard.
    //
    // Apunta a `/dashboard` y no a la raíz, por lo mismo que el Dashboard de la consola:
    // la raíz de MO resuelve por rol y su `**` cae en el formulario de inicio de sesión,
    // así que un tropiezo ahí deja al usuario pidiendo credenciales. Si el rol no puede
    // ver el panel, MO lo reenvía solo a la pantalla que sí le toca.
    externalUrl: `${environment.maintenanceAppUrl}/dashboard`,
  },
  {
    key: 'transportOperations',
    name: 'Transport Operations',
    iconName: 'local_shipping',
    isCurrent: true,
  },
  {
    key: 'automatedHighRiskScreening',
    name: 'Automated High Risk Screening',
    iconName: 'security',
  },
];

const DEFAULT_ICON = 'apps';

/**
 * Icono de marca de la consola, el mismo del favicon en tema oscuro. Es un círculo negro
 * con el logo calado, así que se lee igual sobre la tarjeta clara y sobre la oscura.
 */
const DASHBOARD_LOGO = 'assets/logos/Myservy%20Icon%20white.png';

/**
 * El Dashboard no es una solución del catálogo, así que el backend no lo devuelve en el
 * listado: se agrega a mano y siempre al final. Lleva el icono de Myservy fijo y no la
 * imagen del workspace, que es la del cliente y no la de la consola.
 *
 * Apunta a `/dashboard` y no a la raíz: en la consola la raíz es su formulario de inicio
 * de sesión, que no reenvía al usuario ya autenticado, así que enlazar a `/` pedía
 * credenciales aunque la cookie de sesión viajara bien.
 */
const DASHBOARD_ENTRY = {
  key: 'dashboard',
  name: 'Dashboard',
  iconName: 'dashboard',
  imageUrl: DASHBOARD_LOGO,
  url: `${environment.consoleAppUrl}/dashboard`,
};

/**
 * NFD separa los acentos en marcas combinantes y el filtro final las descarta, de forma
 * que "Operación" y "Operacion" resuelven a la misma clave.
 */
function normalizeKey(value: string): string {
  return value
    .normalize('NFD')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

function findCatalogEntry(name: string, solutionCode?: string): CatalogEntry | null {
  const normalizedName = normalizeKey(name);
  const normalizedCode = solutionCode ? normalizeKey(solutionCode) : '';

  return (
    SOLUTION_CATALOG.find((entry) => normalizeKey(entry.name) === normalizedName) ??
    SOLUTION_CATALOG.find((entry) => !!normalizedCode && normalizeKey(entry.key) === normalizedCode) ??
    SOLUTION_CATALOG.find((entry) => !!normalizedCode && normalizeKey(entry.name) === normalizedCode) ??
    null
  );
}

/** El primer valor con contenido de la lista, ya recortado; null si no hay ninguno. */
function firstNonEmpty(...values: (string | null | undefined)[]): string | null {
  for (const value of values) {
    const trimmed = value?.trim();
    if (trimmed) return trimmed;
  }
  return null;
}

/** True cuando la url no lleva ruta: `https://host` o `https://host/`. */
function hasNoPath(url: URL): boolean {
  return url.pathname === '/' || url.pathname === '';
}

/**
 * Completa la url del backend con la ruta del catálogo cuando aquella es solo el origen.
 *
 * El endpoint envía el host de cada solución —`https://mo-sit.myservy.com/`— pero no la
 * pantalla a la que entrar, y varias soluciones no resuelven su raíz para un usuario ya
 * autenticado: Maintenance Operations la reparte por rol y su `**` cae en el formulario de
 * inicio de sesión. Por eso el host manda —es lo que cambia entre ambientes— y la ruta la
 * pone el catálogo, que es donde sabemos a qué pantalla lleva cada solución.
 *
 * Si el backend ya envía una ruta, esa gana: significa que la solución sí resuelve su
 * destino y el catálogo no tiene nada que añadir.
 */
function withCatalogPath(baseUrl: string, entry: CatalogEntry | null): string {
  if (!entry?.externalUrl) return baseUrl;

  try {
    const url = new URL(baseUrl);
    if (!hasNoPath(url)) return baseUrl;

    const fallback = new URL(entry.externalUrl);
    if (hasNoPath(fallback)) return baseUrl;

    url.pathname = fallback.pathname;
    return url.toString();
  } catch {
    return baseUrl;
  }
}

/**
 * Destino de la solución. `pathUrl` es lo que envía el endpoint, y manda sobre el catálogo
 * local: el catálogo es el respaldo para las soluciones que el backend aún no resuelve.
 *
 * Del catálogo se conserva la ruta cuando el backend envía solo el host, que es el caso de
 * Maintenance Operations: sin `/dashboard` el usuario aterrizaba en su pantalla de inicio
 * de sesión en lugar de en la solución.
 */
function resolveSolutionUrl(solution: Solution | null, entry: CatalogEntry | null): string | null {
  const url = firstNonEmpty(
    solution?.pathUrl,
    solution?.url,
    solution?.externalUrl,
    entry?.externalUrl,
  );

  return url ? withCatalogPath(url, entry) : null;
}

/**
 * Imagen propia de la solución, la que el endpoint envía como `imagePath`.
 *
 * Solo se mira dentro de `solution`: la `imageUrl` de la fila es la del workspace y viene
 * igual en todas, así que pintarla repetiría el mismo logo en cada tarjeta. Cuando no hay
 * ninguna, la tarjeta cae en el icono del catálogo.
 */
function resolveSolutionImage(solution: Solution | null): string | null {
  return firstNonEmpty(
    solution?.imagePath,
    solution?.imageUrl,
    solution?.logoUrl,
    solution?.iconUrl,
  );
}

function toSolutionOption(workspaceSolution: WorkspaceSolution, index: number): SolutionOption | null {
  const solution = workspaceSolution?.solution ?? null;
  const name = solution?.name?.trim() ?? '';
  // Una fila sin nombre no se puede pintar: la respuesta trae solo ids cuando la solución
  // fue dada de baja del catálogo.
  if (!name) return null;

  const entry = findCatalogEntry(name, solution?.solutionCode);

  return {
    key:
      workspaceSolution.workspaceSolutionId ||
      workspaceSolution.id ||
      workspaceSolution.solutionId ||
      `${name}-${index}`,
    name,
    iconName: entry?.iconName ?? DEFAULT_ICON,
    imageUrl: resolveSolutionImage(solution),
    url: resolveSolutionUrl(solution, entry),
    isCurrent: entry?.isCurrent ?? false,
  };
}

/**
 * Compara destinos completos y no orígenes: las soluciones que viven dentro de la consola
 * comparten host con el Dashboard y solo se distinguen por la ruta.
 */
function sameTarget(url: string | null, reference: string): boolean {
  if (!url) return false;
  try {
    return new URL(url).href === new URL(reference).href;
  } catch {
    return false;
  }
}

/**
 * El Dashboard con el icono de Myservy. Si algún día el backend lo devuelve como una
 * solución más, la del listado manda y esta no se agrega, para no ofrecer dos veces el
 * mismo destino.
 */
function toDashboardOption(options: SolutionOption[]): SolutionOption | null {
  if (options.some((option) => sameTarget(option.url, DASHBOARD_ENTRY.url))) return null;

  return { ...DASHBOARD_ENTRY, isCurrent: false };
}

/** Resuelve las soluciones del workspace contra el catálogo, en el orden del backend. */
export function toSolutionOptions(workspaceSolutions: WorkspaceSolution[] | null | undefined): SolutionOption[] {
  const rows = workspaceSolutions ?? [];
  // Sin ninguna fila no hay workspace que mostrar: el modal dice que no hay soluciones
  // habilitadas, y una tarjeta suelta del Dashboard lo contradiría.
  if (!rows.length) return [];

  const options = rows
    .map((workspaceSolution, index) => toSolutionOption(workspaceSolution, index))
    .filter((option): option is SolutionOption => option !== null);

  const dashboard = toDashboardOption(options);
  if (dashboard) options.push(dashboard);

  return options;
}

/**
 * Añade el workspace activo a la url de la solución.
 *
 * Es lo que evita un segundo inicio de sesión: la solución de destino comparte la cookie
 * de sesión del dominio y con este parámetro sabe además en qué workspace entrar, sin
 * preguntarle nada al usuario.
 */
export function appendWorkspaceId(baseUrl: string, workspaceId: string | null): string {
  if (!workspaceId) return baseUrl;

  try {
    const url = new URL(baseUrl);
    url.searchParams.set('workspaceId', workspaceId);
    return url.toString();
  } catch {
    const separator = baseUrl.includes('?') ? '&' : '?';
    return `${baseUrl}${separator}workspaceId=${encodeURIComponent(workspaceId)}`;
  }
}
